import React from 'react';
import Icon from '../AppIcon';

/**
 * EmptyState - brand-styled empty state with illustration, message, and CTA
 * variant: 'projects' | 'contractors' | 'dashboard-projects' | 'dashboard-customers' | 'dashboard-activity' | 'dashboard-deadlines'
 */
const EmptyState = ({ variant = 'projects', onAction, actionLabel, canCreate = true }) => {
  const configs = {
    'projects': {
      icon: 'Briefcase',
      title: 'No projects yet',
      description: 'Start building your ADU pipeline by adding your first project.',
      cta: actionLabel || 'Add your first project',
      iconBg: 'bg-primary/10',
      iconColor: 'var(--color-primary)',
      accentBar: true,
    },
    'contractors': {
      icon: 'HardHat',
      title: 'No contractors found',
      description: 'Build your trusted contractor network to assign to ADU projects.',
      cta: actionLabel || 'Add your first contractor',
      iconBg: 'bg-accent/10',
      iconColor: 'var(--color-accent)',
      accentBar: true,
    },
    'contractors-filtered': {
      icon: 'SearchX',
      title: 'No contractors match your filters',
      description: 'Try adjusting your search or filter criteria to find contractors.',
      cta: null,
      iconBg: 'bg-muted',
      iconColor: 'var(--color-muted-foreground)',
      accentBar: false,
    },
    'projects-filtered': {
      icon: 'SearchX',
      title: 'No projects match your filters',
      description: 'Try adjusting your search or filter criteria to find projects.',
      cta: null,
      iconBg: 'bg-muted',
      iconColor: 'var(--color-muted-foreground)',
      accentBar: false,
    },
    'dashboard-projects': {
      icon: 'Briefcase',
      title: 'No active projects',
      description: 'Create your first ADU project to start tracking progress.',
      cta: actionLabel || 'New Project',
      iconBg: 'bg-primary/10',
      iconColor: 'var(--color-primary)',
      accentBar: false,
    },
    'dashboard-customers': {
      icon: 'Users',
      title: 'No customers yet',
      description: 'Add your first customer to begin managing ADU inquiries.',
      cta: actionLabel || 'Add Customer',
      iconBg: 'bg-accent/10',
      iconColor: 'var(--color-accent)',
      accentBar: false,
    },
    'dashboard-activity': {
      icon: 'Activity',
      title: 'No recent activity',
      description: 'Activity will appear here as your team works on projects.',
      cta: null,
      iconBg: 'bg-muted',
      iconColor: 'var(--color-muted-foreground)',
      accentBar: false,
    },
    'dashboard-deadlines': {
      icon: 'CheckCircle',
      title: 'No upcoming deadlines',
      description: "You\'re all caught up! No permit or event deadlines in the next 30 days.",
      cta: null,
      iconBg: 'bg-success/10',
      iconColor: 'var(--color-success)',
      accentBar: false,
    },
  };

  const config = configs?.[variant] || configs?.['projects'];
  const isCompact = variant?.startsWith('dashboard-');

  return (
    <div className={`flex flex-col items-center justify-center text-center ${
      isCompact ? 'py-8 px-4' : 'py-14 px-6'
    }`}>
      {/* Illustration area */}
      <div className="relative mb-4">
        {config?.accentBar && (
          <div className="absolute -top-2 left-1/2 -translate-x-1/2 w-12 h-1 rounded-full bg-accent" />
        )}
        <div className={`${
          isCompact ? 'w-14 h-14' : 'w-20 h-20'
        } rounded-2xl ${config?.iconBg} flex items-center justify-center mx-auto shadow-sm`}>
          <Icon
            name={config?.icon}
            size={isCompact ? 26 : 36}
            color={config?.iconColor}
          />
        </div>
      </div>
      {/* Text */}
      <h3 className={`font-semibold text-foreground mb-1 ${
        isCompact ? 'text-sm' : 'text-lg'
      }`}>
        {config?.title}
      </h3>
      <p className={`text-muted-foreground max-w-xs ${
        isCompact ? 'text-xs' : 'text-sm'
      }`}>
        {config?.description}
      </p>
      {/* CTA Button */}
      {config?.cta && canCreate && onAction && (
        <button
          onClick={onAction}
          className={`mt-4 inline-flex items-center gap-2 font-medium rounded-lg transition-colors ${
            isCompact
              ? 'px-3 py-1.5 text-xs bg-primary text-primary-foreground hover:bg-primary/90'
              : 'px-5 py-2.5 text-sm bg-primary text-primary-foreground hover:bg-primary/90'
          }`}
        >
          <Icon name="Plus" size={isCompact ? 12 : 15} />
          {config?.cta}
        </button>
      )}
    </div>
  );
};

export default EmptyState;
